import { Box, CircularProgress, Button } from '@mui/material';
import CustomTypography from '../ui/atoms/CustomTypography';
import useWorkOrderSubmission from '../hooks/useWorkOrderSubmissions';
import { useCallback, useEffect, useMemo } from 'react';
import { useAppSelector } from '../redux/Store';
import { createColumnHelper } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { useFindPermissions } from '../utils/helpers/useFindPermissions';
import CustomTable from '../ui/organisms/Table/Table';
import {
  resetIsGalleryPanelOpen,
  setContextId,
  setQueryObj,
} from '../redux/reducer/contextIdReducer';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { decryptJSON, encryptJSON } from '../utils/helpers/Encryption';
import { useParams } from 'react-router-dom';
import { setOrder } from '../redux/reducer/inspact/order_reducer';

const Submissions = () => {
  const currentOrder = useAppSelector(
    (state) => state.inspactOrder.currentOrder
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    getWorkOrderSubmissions,
    getWorkOrderSubmissionsResults,
    getWorkOrderSubmissionsPending,
  } = useWorkOrderSubmission();
  // const [submissions, setSubmissions] = useState<Array<any>>([]);
  // const [loading, setLoading] = useState<boolean>(false);
  const {
    canViewAuditResultsById,
    canViewWorkOrderLiteLuggage,
    canViewSubmissions,
    isInspector,
  } = useFindPermissions();

  const { orderNumber, propertyLoanId } = useParams<{
    orderNumber?: string;
    propertyLoanId?: string;
  }>(); // Ensure correct types; // ✅ Fetch orderNumber from URL
  const decryptOrderNumber = orderNumber || '';
  const decryptPropertyLoanId = propertyLoanId || '';
  if (
    decryptOrderNumber &&
    decryptPropertyLoanId &&
    currentOrder?.orderNumber !== decryptOrderNumber &&
    currentOrder?.propertyLoanId !== decryptPropertyLoanId
  ) {
    dispatch(
      setOrder({
        orderNumber: decryptOrderNumber,
        propertyLoanId: decryptPropertyLoanId,
      })
    );
  }

  const handleNavigateToAuditView = useCallback(
    (submissionId: string) => {
      dispatch(resetIsGalleryPanelOpen());
      dispatch(setContextId(submissionId));
      dispatch(
        setQueryObj({
          context: 'AuditResultsBySubmissionId',
          key: 'Submission.ID',
          id: submissionId,
        })
      );

      navigate(
        `/non-audit-view?orderNumber=${currentOrder?.orderNumber}&&propertyLoanId=${currentOrder?.propertyLoanId}&&submissionId=${submissionId}`
      );
    },
    [
      currentOrder?.orderNumber,
      currentOrder?.propertyLoanId,
      dispatch,
      navigate,
    ]
  );

  useEffect(() => {
    if (
      getWorkOrderSubmissionsResults &&
      getWorkOrderSubmissionsResults?.submissions?.length === 1 &&
      canViewAuditResultsById
    ) {
      handleNavigateToAuditView(
        getWorkOrderSubmissionsResults?.submissions[0]?.submissionId
      );
    }
  }, [
    canViewAuditResultsById,
    getWorkOrderSubmissionsResults,
    handleNavigateToAuditView,
  ]);

  useEffect(() => {
    if (!currentOrder?.orderNumber) return;

    // setLoading(true); // Start loading
    if (canViewSubmissions)
      getWorkOrderSubmissions(currentOrder?.orderNumber || '');
    // .then((result) => {
    //   if (result?.submissions) {
    //     setSubmissions(result.submissions);
    //   }
    // })
    // .catch(() => {
    //   setSubmissions([]);
    // })
    // .finally(() => {
    //   setLoading(false); // Stop loading
    // });
  }, [currentOrder, getWorkOrderSubmissions, canViewSubmissions]);

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<any>();
    const baseColumns = [
      columnHelper.accessor('submissionType', {
        header: 'Submission Type',
        cell: (info) => (
          <CustomTypography
            variant='subtitle2'
            sx={{ color: '#333', lineHeight: '1.42px', fontSize: '0.8rem' }}
          >
            {info?.getValue() || 'N/A'}
          </CustomTypography>
        ),
      }),
      columnHelper.accessor('mainCode', {
        header: 'Main Code',
        cell: (info) => (
          <CustomTypography
            variant='subtitle2'
            sx={{ color: '#333', lineHeight: '1.42px', fontSize: '0.8rem' }}
          >
            {info?.getValue() || 'N/A'}
          </CustomTypography>
        ),
      }),
      columnHelper.accessor('submittedBy', {
        header: 'Submitted By',
        cell: (info) => (
          <CustomTypography
            variant='subtitle2'
            sx={{ color: '#333', lineHeight: '1.42px', fontSize: '0.8rem' }}
          >
            {info?.getValue() || 'N/A'}
          </CustomTypography>
        ),
      }),
      columnHelper.accessor('scriptVersion', {
        header: 'Script Version',
        cell: (info) => (
          <CustomTypography
            variant='subtitle2'
            sx={{ color: '#333', lineHeight: '1.42px', fontSize: '0.8rem' }}
          >
            {info?.getValue() || 'N/A'}
          </CustomTypography>
        ),
      }),
      columnHelper.accessor('submissionCreateDate', {
        header: 'Submission Create DT',
        cell: (info) => (
          <CustomTypography
            variant='subtitle2'
            sx={{ color: '#333', lineHeight: '1.42px', fontSize: '0.8rem' }}
          >
            {dayjs(info?.getValue()).format('MM/DD/YYYY h:mm A')}
          </CustomTypography>
        ),
      }),
      columnHelper.accessor('vendorSubmittedDate', {
        header: 'Vendor Submitted DT',
        cell: (info) => (
          <CustomTypography
            variant='subtitle2'
            sx={{ color: '#333', lineHeight: '1.42px', fontSize: '0.8rem' }}
          >
            {info?.getValue()
              ? dayjs(info?.getValue()).format('MM/DD/YYYY h:mm A')
              : 'N/A'}
          </CustomTypography>
        ),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => {
          const submissionId = info.row.original?.submissionId;
          return canViewAuditResultsById ? (
            <Link
              to={`/non-audit-view?orderNumber=${currentOrder?.orderNumber}&&submissionId=${submissionId}`}
              onClick={() => handleNavigateToAuditView(submissionId)}
              style={{
                color: '#1E88E5',
                textDecoration: 'none',
                fontSize: '0.8rem',
                fontWeight: 500,
              }}
            >
              Viewable
            </Link>
          ) : (
            <CustomTypography
              variant='subtitle2'
              sx={{ color: '#333', lineHeight: '1.42px', fontSize: '0.8rem' }}
            >
              N/A
            </CustomTypography>
          );
        },
      }),
    ];

    if (!isInspector) {
      baseColumns.splice(
        5,
        0,
        columnHelper.accessor('auditCompleteDT', {
          header: 'Audit Complete DT',
          cell: (info) => (
            <CustomTypography
              variant='subtitle2'
              sx={{ color: '#333', lineHeight: '1.42px', fontSize: '0.8rem' }}
            >
              {info?.getValue()
                ? dayjs(info?.getValue()).format('MM/DD/YYYY h:mm A')
                : 'N/A'}
            </CustomTypography>
          ),
        })
      );
    }

    return baseColumns;
  }, [
    canViewAuditResultsById,
    currentOrder?.orderNumber,
    handleNavigateToAuditView,
    isInspector,
  ]);

  const onRowHandleClick = (row: any) => {
    if (canViewAuditResultsById) handleNavigateToAuditView(row?.submissionId);
  };

  return (
    <Box sx={{ padding: 4, height: '100vh', overflow: 'auto' }}>
      <CustomTypography
        variant='h5'
        sx={{
          color: '#343330',
          fontFamily: 'Montserrat',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: '600',
          lineHeight: 'normal',
          textTransform: 'capitalize',
        }}
      >
        Work Order Submissions
      </CustomTypography>

      {getWorkOrderSubmissionsPending && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100vh',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1300, // Ensures it's above everything
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}
      {!getWorkOrderSubmissionsPending &&
      (!getWorkOrderSubmissionsResults ||
        Object.keys(getWorkOrderSubmissionsResults).length === 0) ? (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CustomTypography variant='body1'>No results found</CustomTypography>
        </Box>
      ) : (
        <CustomTable
          data={getWorkOrderSubmissionsResults?.submissions || []}
          columns={columns}
          onRowClick={onRowHandleClick}
        />
      )}
    </Box>
  );
};

export default Submissions;
